import {
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js';
import type { valueOf } from '../types/common/utility';
import {
  getIndustryCodePath,
  getIndustryPrimaryDescriptionPath,
  getIndustrySecondaryDescriptionPath,
  ISO_COUNTRY_BY_LANGUAGE,
} from './country';

export const languages = Object.freeze({
  'en-US': 'en-US',
  'en-GB': 'en-GB',
  'en-AU': 'en-AU',
});

export type Languages = valueOf<typeof languages>;

export type Region = {
  isUS: boolean;
  isUK: boolean;
  isAU: boolean;
};

export const COUNTRIES = Object.freeze({
  US: 'US',
  UK: 'UK',
  AU: 'AU',
});

export type Countries = (typeof COUNTRIES)[keyof typeof COUNTRIES];

export const REGION_FLAGS = {
  [languages['en-US']]: '🇺🇸',
  [languages['en-GB']]: '🇬🇧',
  [languages['en-AU']]: '🇦🇺',
};

export const REGION_OPTIONS = [
  {
    label: `US`,
    value: languages['en-US'],
    flag: REGION_FLAGS['en-US'],
  },
  {
    label: `UK`,
    value: languages['en-GB'],
    flag: REGION_FLAGS['en-GB'],
  },
  {
    label: `AU`,
    value: languages['en-AU'],
    flag: REGION_FLAGS['en-AU'],
  },
] as const;

/**
 * Country (in any spelling we have seen stored) to platform language, for phone and address
 * formatting of data coming back from the API.
 *
 * ISO `GB` / `GBR` were missing until PS-8284, so any record already holding the correct ISO code —
 * every NAD result from `master-accounts` — fell through to `en-US` and was formatted as a US
 * address with a US phone number.
 */
export const COUNTRY_MAPPING = Object.freeze({
  UK: languages['en-GB'],
  GB: languages['en-GB'],
  GBR: languages['en-GB'],
  'United Kingdom': languages['en-GB'],
  'UNITED KINGDOM': languages['en-GB'],
  US: languages['en-US'],
  'United States': languages['en-US'],
  'UNITED STATES': languages['en-US'],
  AUS: languages['en-AU'],
  AU: languages['en-AU'],
  Australia: languages['en-AU'],
  AUSTRALIA: languages['en-AU'],
});

export const REGION_COUNTRY_CODE_MAPPING = Object.freeze({
  [languages['en-GB']]: '+44',
  [languages['en-US']]: '+1',
  [languages['en-AU']]: '+61',
});

export type CountryCodes =
  (typeof REGION_COUNTRY_CODE_MAPPING)[keyof typeof REGION_COUNTRY_CODE_MAPPING];

/* Country Code Selector */
export const COUNTRY_CODES_SELECT_OPTIONS = [
  {
    value: REGION_COUNTRY_CODE_MAPPING['en-US'],
    label: `United States (+1)`,
  },
  {
    value: REGION_COUNTRY_CODE_MAPPING['en-GB'],
    label: `United Kingdom (+44)`,
  },
  {
    value: REGION_COUNTRY_CODE_MAPPING['en-AU'],
    label: `Australia (+61)`,
  },
] as const;

export type CountryCodeSelectOption = { value: string; label: string };

/**
 * Every dial code, for forms that must accept a number outside the three
 * regions Cowbell trades in — an agency's overseas staff, for instance.
 *
 * Derived from libphonenumber-js rather than hand-written, because a
 * hand-written list of ~240 countries is exactly the kind of table that goes
 * stale silently. The three regions above stay pinned at the top so the common
 * choices remain the first ones a user sees.
 *
 * Deduplicated by dial code: +1 is shared by the US, Canada and much of the
 * Caribbean, and the Select's value IS the dial code, so duplicates would
 * collide. The pinned entries win their own codes.
 */
export const INTERNATIONAL_COUNTRY_CODES_SELECT_OPTIONS: CountryCodeSelectOption[] =
  (() => {
    const pinned: CountryCodeSelectOption[] = COUNTRY_CODES_SELECT_OPTIONS.map(
      (option) => ({ value: option.value, label: option.label })
    );
    const seen = new Set(pinned.map((option) => option.value));
    let regionNames: Intl.DisplayNames | undefined;
    try {
      regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      // Intl.DisplayNames is unavailable on this runtime; fall back to the ISO
      // code as the label rather than dropping the country entirely.
      regionNames = undefined;
    }

    const rest = getCountries()
      .map((country) => {
        const value = `+${getCountryCallingCode(country)}`;
        const name = regionNames?.of(country) ?? country;
        return { value, label: `${name} (${value})` };
      })
      .filter((option) => {
        if (seen.has(option.value)) {
          return false;
        }
        seen.add(option.value);
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    return [...pinned, ...rest];
  })();

/**
 * Industry taxonomy selection now lives in `./country`, keyed by ISO country and normalising its
 * input, so a record holding `GB` resolves to SIC rather than silently falling back to NAICS.
 * Re-exported here so existing import sites are unaffected.
 */
export {
  getIndustryCodePath,
  getIndustryPrimaryDescriptionPath,
  getIndustrySecondaryDescriptionPath,
};

export const COUNTRY_BY_COUNTRY_CODE = Object.freeze({
  [REGION_COUNTRY_CODE_MAPPING['en-GB']]: languages['en-GB'],
  [REGION_COUNTRY_CODE_MAPPING['en-US']]: languages['en-US'],
  [REGION_COUNTRY_CODE_MAPPING['en-AU']]: languages['en-AU'],
});

export const deriveLanguageFromCountryCode = (
  countryCode: CountryCodes | undefined
) => {
  if (!countryCode) {
    return null;
  }

  return COUNTRY_BY_COUNTRY_CODE[countryCode] ?? null;
};

/**
 * The ISO country per language, for libphonenumber. This was already the correct ISO map while
 * `LANGUAGE_COUNTRY_MAPPING` sat 90 lines above it returning `UK` for the same input — the two
 * differed in exactly one entry, and that entry was the bug. Now aliased to the single source.
 */
export const INTERNATIONAL_PHONE_COUNTRY = ISO_COUNTRY_BY_LANGUAGE as Record<
  string,
  CountryCode
>;
